import type { WorkspaceRole } from '@reelplicate/api-client/model';
import type { BrandMarkName } from '@reelplicate/ui';

/**
 * The MCP server agents connect to. Production: the MCP host; locally the API
 * itself (VITE_MCP_URL in .env).
 */
export const MCP_URL =
  (import.meta.env.VITE_MCP_URL as string | undefined) ?? 'https://mcp.reelplicate.com';

/** The URL as shown: without the scheme. */
export const MCP_URL_LABEL = MCP_URL.replace(/^https?:\/\//, '');

const MARKS: [RegExp, BrandMarkName][] = [
  [/claude/i, 'claude'],
  [/chatgpt|openai/i, 'chatgpt'],
  [/cursor/i, 'cursor'],
  [/gemini/i, 'gemini'],
  [/grok/i, 'grok'],
  [/n8n/i, 'n8n'],
];

/** The logo for an agent's registered name ("Claude Code" → claude), if we have one. */
export function agentMark(name: string): BrandMarkName | null {
  return MARKS.find(([pattern]) => pattern.test(name))?.[1] ?? null;
}

/** What an agent with each role can do, for the role pickers. */
export const AGENT_ROLES: Record<WorkspaceRole, { name: string; description: string }> = {
  OWNER: {
    name: 'Owner',
    description: 'Reads everything, makes content and reviews drafts.',
  },
  EDITOR: {
    name: 'Editor',
    description: 'Reads everything, runs flows, schedules posts. Can’t approve drafts.',
  },
  REVIEWER: {
    name: 'Reviewer',
    description: 'Reads everything and approves or rejects drafts. Can’t make content.',
  },
};

/** Owners can give an agent any role; everyone else only their own. */
export function grantableRoles(role: WorkspaceRole | undefined): WorkspaceRole[] {
  if (!role) return [];
  return role === 'OWNER' ? ['OWNER', 'EDITOR', 'REVIEWER'] : [role];
}

const SHORT_DATE = new Intl.DateTimeFormat('en-US', { day: 'numeric', month: 'short' });

/** "12 Sep", as the designs write dates (en-GB would say "Sept"). */
export function shortDate(date: string | Date): string {
  const parts = Object.fromEntries(
    SHORT_DATE.formatToParts(new Date(date)).map((part) => [part.type, part.value]),
  );
  return `${parts.day} ${parts.month}`;
}
